import { RefObject, useEffect, useRef } from "react";

/** Closes a portaled panel on page/ancestor scroll; the panel's own list stays scrollable. */
export const useCloseOnScroll = (
  open: boolean,
  close: () => void,
  // Only `parentElement` is read, so any Element works.
  anchorRef: RefObject<Element | null>,
  /** Scroll starting inside a match does not close. */
  ignoreSelector?: string
) => {
  // In a ref so re-renders don't reinstall the listeners and race with opening.
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!open) return;

    const baselines = new Map<HTMLElement | Window, { x: number; y: number }>();
    baselines.set(window, { x: window.scrollX, y: window.scrollY });

    const handleScroll = (event: Event) => {
      const target = event.target;

      if (ignoreSelector && target instanceof Element && target.closest(ignoreSelector)) return;

      // Page scroll: `document` (and jsdom's own target) are not elements.
      if (!(target instanceof Element)) {
        const last = baselines.get(window)!;
        if (window.scrollX !== last.x || window.scrollY !== last.y) closeRef.current();
        baselines.set(window, { x: window.scrollX, y: window.scrollY });
        return;
      }

      if (!(target instanceof HTMLElement)) return;

      const last = baselines.get(target);
      // Untracked — e.g. the panel's own list.
      if (!last) return;

      if (target.scrollLeft !== last.x || target.scrollTop !== last.y) closeRef.current();
      baselines.set(target, { x: target.scrollLeft, y: target.scrollTop });
    };

    window.addEventListener("scroll", handleScroll, { capture: true });

    const trackedAncestors: HTMLElement[] = [];
    let ancestor = anchorRef.current?.parentElement;

    while (ancestor) {
      const scrollable =
        ancestor.scrollWidth > ancestor.clientWidth ||
        ancestor.scrollHeight > ancestor.clientHeight;

      if (scrollable) {
        baselines.set(ancestor, { x: ancestor.scrollLeft, y: ancestor.scrollTop });
        ancestor.addEventListener("scroll", handleScroll, { capture: true });
        trackedAncestors.push(ancestor);
      }

      ancestor = ancestor.parentElement;
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, { capture: true });
      trackedAncestors.forEach((element) =>
        element.removeEventListener("scroll", handleScroll, { capture: true })
      );
    };
  }, [open, anchorRef, ignoreSelector]);
};
